import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { formatMoney, formatPercent } from "nemuru-components";
import React, { useContext } from "react";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import MainButton from "../../../shared/components/MainButton";
import Text from "../../../shared/components/Text";
import { usePricings } from "../../loanApplication/SummaryAndAdjust/shared/hooks/usePricings";
import { getMinDownPaymentValueByAssetAmount } from "./utils/downPaymentCalculation";
import { InputFieldWithSlider } from "../../../shared/components/InputFieldWithSlider";
import { HintDownPaymentImportLabel } from "./components/HintDownPaymentImportLabel";
import { HintDownPaymentImportLessThanMin } from "./components/HintDownPaymentImportLessThanMin";
import { HintDownPaymentImportGreaterThanMax } from "./components/HintDownPaymentImportGreaterThanMax";
import { useDownPayment } from "./hooks/useDownPayment";
import { getFeeAmountAndInsuranceConditionsText } from "./utils/getFeeAmountAndInsuranceConditionsText";
import { LabelAndValueInfoRow } from "./components/LabelAndValueInfoRow";
import { ConfigContext } from "../../../../contexts/ConfigContext";

export default function DownPaymentLoanAdjust() {
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("sm"));
  const { pricingByLoanId } = usePricings();
  const {
    state: { loan },
    dispatch,
  } = useContext(ProcessContext);
  const {
    state: { dictionaryValues },
  } = useContext(ConfigContext);

  const {
    principal,
    loanConditions,
    setDownPaymentStateRef,
    onSubmitModule,
    handleChangeCommitted,
    getSliderTermAndSetLoanConditions,
    totalAmountToPay,
    setupFeePercentage,
    term,
    downPayment,
    autoAdjustedTerm,
    disableDownPayment,
    isFaciliteaAgent,
    loading,
  } = useDownPayment({ loan, dispatch });

  const {
    selectedDownPayment,
    maxAutoDownPayment,
    isDownPaymentOutOfScope,
    downPaymentGreaterThanMax,
    downPaymentLessThanMin,
  } = downPayment;

  const { selectedTerm, minTerm, termMarks, allowedMaxTerm, termOutOfScope } =
    term;

  const autoPrice = loan.asset_details.total_budget.amount;

  if (!pricingByLoanId || !minTerm || !allowedMaxTerm)
    return (
      <>
        <Box />
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          height={"100%"}
          gridGap={16}
        >
          <CircularProgress size={20} />
          <Text tId={"LOADING_LOAN_CONDITIONS"} />
        </Box>
        <Box />
      </>
    );

  return (
    <>
      <Box
        py={1}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
      >
        {loading ? (
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            gridGap={"12px"}
            minHeight={isMobile ? "70px" : "84px"}
          >
            <CircularProgress size={24} />
            <Typography
              color={"textSecondary"}
              variant={"subtitle1"}
              align={"center"}
            >
              <Text tId={"CALCULATING_CONDITIONS"} />
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              style={{ color: palette.text.title }}
              variant={isMobile ? "h4" : "h3"}
              align={"center"}
            >
              <Text
                strong
                tId={"MONEY_PER_MONTH"}
                dynamicDataTexts={[
                  isDownPaymentOutOfScope
                    ? "- €"
                    : formatMoney(loanConditions.quota, 2),
                ]}
              />
            </Typography>
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"center"}
            >
              <Typography
                color={"textSecondary"}
                variant={"subtitle1"}
                align={"center"}
              >
                <Text tId={"TIN"} />: {formatPercent(loanConditions.tin)} |{" "}
                <Text tId={"TAE"} />: {formatPercent(loanConditions.tae)}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      {!isFaciliteaAgent && (
        <Box pb={3}>
          <Typography
            align={"center"}
            variant={"body2"}
            color={"textSecondary"}
          >
            <Text tId={"PLEASE_SELECT_DOWN_PAYMENT_AND_TERM"} />
          </Typography>
        </Box>
      )}
      <Grid container spacing={2}>
        {!disableDownPayment && (
          <Grid item xs={12} md={6} id={"downPaymentInputField"}>
            <Box display={"flex"} flexDirection={"column"}>
              <InputFieldWithSlider
                disabled={disableDownPayment}
                labelTid={"DOWN_PAYMENT"}
                value={selectedDownPayment.value}
                onChange={setDownPaymentStateRef}
                onChangeCommitted={handleChangeCommitted}
                min={getMinDownPaymentValueByAssetAmount(autoPrice)}
                max={maxAutoDownPayment}
                InputProps={{
                  startAdornment: <Box mr={1}>€</Box>,
                  endAdornment: (
                    <i
                      className="icon-edit-input"
                      style={{
                        pointerEvents: "none",
                        opacity: 0.5,
                        verticalAlign: "text-top",
                        fontSize: "25px",
                        color: palette.grey[700],
                      }}
                    />
                  ),
                }}
                hintLabel={
                  selectedDownPayment.invalidNumber ? (
                    <Text>{"INVALID_VALUE"}</Text>
                  ) : downPaymentGreaterThanMax ? (
                    <HintDownPaymentImportGreaterThanMax
                      maxAutoDownPayment={maxAutoDownPayment}
                    />
                  ) : downPaymentLessThanMin ? (
                    <HintDownPaymentImportLessThanMin autoPrice={autoPrice} />
                  ) : (
                    <HintDownPaymentImportLabel
                      autoPrice={autoPrice}
                      maxAutoDownPayment={maxAutoDownPayment}
                    />
                  )
                }
                error={selectedDownPayment.invalidNumber}
              />
            </Box>
          </Grid>
        )}
        <Grid item xs={12} md={disableDownPayment ? 12 : 6}>
          <Box display={"flex"} flexDirection={"column"}>
            <InputFieldWithSlider
              labelTid={"TERM"}
              value={
                selectedTerm.value > allowedMaxTerm
                  ? allowedMaxTerm
                  : selectedTerm.value
              }
              onChange={getSliderTermAndSetLoanConditions}
              onChangeCommitted={getSliderTermAndSetLoanConditions}
              min={minTerm}
              max={allowedMaxTerm}
              step={5}
              termMarks={termMarks}
              // disabled={!!termMarks}
              error={selectedTerm.invalidNumber || selectedTerm.invalidValue}
              InputProps={{
                endAdornment: (
                  <>
                    <Box mr={2}>{dictionaryValues["MONTHS"]}</Box>
                    {!termMarks && (
                      <i
                        className="icon-edit-input"
                        style={{
                          pointerEvents: "none",
                          opacity: 0.5,
                          verticalAlign: "text-top",
                          fontSize: "25px",
                          color: palette.grey[700],
                        }}
                      />
                    )}
                  </>
                ),
              }}
              hintLabel={
                selectedTerm.invalidNumber ? (
                  <Text>{"INVALID_VALUE"}</Text>
                ) : termOutOfScope ? (
                  <Text
                    tId={"TERM_MUST_BE_BETWEEN_X_AND_Y"}
                    dynamicDataTexts={[minTerm, allowedMaxTerm]}
                  />
                ) : selectedTerm.invalidValue ? (
                  <Text tId={"INVALID_TERM_VALUE"} />
                ) : (
                  <Text
                    tId={"FROM_X_TO_Y_MONTHS"}
                    dynamicDataTexts={[minTerm, allowedMaxTerm]}
                  />
                )
              }
            />
            {autoAdjustedTerm && (
              <Typography variant="caption" color="textSecondary" align="right">
                <Text tId={"AUTO_ADJUSTED_TERM"} />
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box pb={1}>
        <LabelAndValueInfoRow
          labelTextId={"VEHICLE_PRICE_WITH_TAX"}
          value={formatMoney(autoPrice, 2)}
        />
        <LabelAndValueInfoRow
          labelTextId={"DOWN_PAYMENT"}
          value={
            isDownPaymentOutOfScope
              ? "-"
              : formatMoney(selectedDownPayment.value, 2)
          }
          tooltip={disableDownPayment && "DOWNPAYMENT_NOT_ALLOWED"}
        />
        <LabelAndValueInfoRow
          labelTextId={"APPLICATION_AMOUNT"}
          value={isDownPaymentOutOfScope ? "-" : formatMoney(principal, 2)}
        />
        <LabelAndValueInfoRow
          labelTextId={"TOTAL_PRICE_IN_SPLIT_PAYMENT"}
          value={
            isDownPaymentOutOfScope ? "-" : formatMoney(totalAmountToPay, 2)
          }
        />
      </Box>
      {setupFeePercentage > 0 && (
        <Box>
          <Typography variant={"caption"} color={"textSecondary"}>
            {getFeeAmountAndInsuranceConditionsText(
              loanConditions.setupFeeType,
              setupFeePercentage
            )}
          </Typography>
        </Box>
      )}
      <MainButton
        disabled={
          loading ||
          isDownPaymentOutOfScope ||
          termOutOfScope ||
          selectedTerm.invalidValue
        }
        onClick={onSubmitModule}
      >
        <Text tId="CONTINUE" />
      </MainButton>
    </>
  );
}
